/**
 * Native runtime bridge for Capacitor shells.
 *
 * All Capacitor imports are dynamic and guarded by isNative(): the same
 * code ships to the browser (where these plugins are inert) and to the
 * native iOS/Android shells (where they wire up push, deep links, status
 * bar, and lifecycle). Never top-level import a Capacitor plugin from a
 * module that renders in the browser build.
 */
import { supabase } from "@/integrations/supabase/client";

let bootstrapped = false;

export function isNative(): boolean {
  if (typeof window === "undefined") return false;
  // Capacitor injects window.Capacitor when running inside a native shell.
  const cap = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  return Boolean(cap?.isNativePlatform?.());
}

export function nativePlatform(): "ios" | "android" | "web" {
  if (typeof window === "undefined") return "web";
  const cap = (window as unknown as { Capacitor?: { getPlatform?: () => string } }).Capacitor;
  const p = cap?.getPlatform?.();
  return p === "ios" || p === "android" ? p : "web";
}

/**
 * Initialize native integrations once, after the router mounts.
 * Safe to call in the browser — becomes a no-op there.
 */
export async function initNativeBridge(): Promise<void> {
  if (bootstrapped || !isNative()) return;
  bootstrapped = true;

  // RevenueCat: identify the current Supabase user to the IAP SDK so purchases
  // and webhooks map back to auth.uid(). Safe no-op when SDK keys are absent.
  try {
    const { loginRevenueCat, logoutRevenueCat } = await import("@/lib/entitlements/revenuecat");
    const { data } = await supabase.auth.getUser();
    if (data.user?.id) await loginRevenueCat(data.user.id);
    supabase.auth.onAuthStateChange((_evt, session) => {
      if (session?.user?.id) void loginRevenueCat(session.user.id);
      else void logoutRevenueCat();
    });
  } catch {
    /* revenuecat init is best-effort */
  }


  try {
    // Status bar
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: "#080808" }).catch(() => {});

    // Splash screen — hide after hydration.
    const { SplashScreen } = await import("@capacitor/splash-screen");
    await SplashScreen.hide({ fadeOutDuration: 400 }).catch(() => {});

    // Deep links: kafrequency://gate/1 → /gates/1
    const { App } = await import("@capacitor/app");
    App.addListener("appUrlOpen", (event) => {
      try {
        const url = new URL(event.url);
        // Custom scheme kafrequency://path or https://buddhaka.com/path
        const path = url.pathname && url.pathname !== "/" ? url.pathname : `/${url.host}`;
        if (path && path.startsWith("/")) {
          window.location.assign(path + url.search + url.hash);
        }
      } catch {
        /* ignore malformed URLs */
      }
    });

    // Push notifications: register token, persist to Supabase for the signed-in user.
    const { PushNotifications } = await import("@capacitor/push-notifications");
    const permission = await PushNotifications.checkPermissions();
    if (permission.receive === "prompt") {
      await PushNotifications.requestPermissions();
    }
    const granted = (await PushNotifications.checkPermissions()).receive === "granted";
    if (granted) {
      await PushNotifications.register();
      PushNotifications.addListener("registration", async (token) => {
        try {
          const { data } = await supabase.auth.getUser();
          if (!data.user) return;
          await supabase.from("push_subscriptions").upsert(
            {
              user_id: data.user.id,
              endpoint: `native:${nativePlatform()}:${token.value}`,
              p256dh: "",
              auth_secret: "",
            },
            { onConflict: "endpoint" },
          );
        } catch {
          /* silent — retried on next launch */
        }
      });
      PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
        const url = (action.notification.data as { url?: string })?.url;
        if (typeof url === "string" && url.startsWith("/")) {
          window.location.assign(url);
        }
      });
    }
  } catch (err) {
    console.warn("[native] bridge init failed", err);
  }
}
